import { cn } from "@dd/ui";
import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";

const logoCache = new Map<string, string>();
const logoRequests = new Map<string, Promise<string>>();

function logoCacheKey(organizationId?: string, version?: number | null) {
  return `ronda:organization-logo:${organizationId ?? "active"}:${version ?? "current"}`;
}

function cachedLogo(key: string) {
  const memory = logoCache.get(key);
  if (memory) return memory;
  try {
    const stored = sessionStorage.getItem(key);
    if (stored) logoCache.set(key, stored);
    return stored;
  } catch {
    return null;
  }
}

function blobAsDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No pudimos leer la imagen."));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}

function loadLogo(key: string, organizationId?: string) {
  const pending = logoRequests.get(key);
  if (pending) return pending;
  const request = api.organization
    .logo(organizationId)
    .then(blobAsDataUrl)
    .then(async (dataUrl) => {
      const image = new Image();
      image.src = dataUrl;
      await image.decode();
      logoCache.set(key, dataUrl);
      try {
        sessionStorage.setItem(key, dataUrl);
      } catch {
        // A logo is optional polish. A full/disabled storage area must not
        // break the organization switcher.
      }
      logoRequests.delete(key);
      return dataUrl;
    })
    .catch((error) => {
      logoRequests.delete(key);
      throw error;
    });
  logoRequests.set(key, request);
  return request;
}

export async function prepareOrganizationLogo(file: File) {
  if (!file.type.startsWith("image/")) throw new Error("Elegí una imagen.");
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("No pudimos procesar la imagen.");
  context.drawImage(
    bitmap,
    (bitmap.width - side) / 2,
    (bitmap.height - side) / 2,
    side,
    side,
    0,
    0,
    512,
    512,
  );
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", 0.9),
  );
  if (!blob) throw new Error("No pudimos procesar la imagen.");
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let index = 0; index < bytes.length; index += 8192)
    binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
  return { mimeType: "image/webp", data: btoa(binary) };
}

export function OrganizationLogo({
  name,
  hasLogo,
  version,
  organizationId,
  className,
}: {
  name: string;
  hasLogo: boolean;
  version?: number | null;
  organizationId?: string;
  className?: string;
}) {
  const key = logoCacheKey(organizationId, version);
  const [url, setUrl] = useState<string | null>(() => cachedLogo(key));
  useEffect(() => {
    if (!hasLogo) return setUrl(null);
    let cancelled = false;
    const existing = cachedLogo(key);
    if (existing) {
      setUrl(existing);
      return;
    }
    setUrl(null);
    void loadLogo(key, organizationId)
      .then((dataUrl) => {
        if (!cancelled) setUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [hasLogo, key, organizationId]);
  return url ? (
    <img src={url} alt="" className={cn("object-cover", className)} />
  ) : (
    <span
      className={cn(
        "grid place-items-center bg-primary font-semibold text-primary-foreground",
        className,
      )}
    >
      {name.slice(0, 2).toUpperCase()}
    </span>
  );
}
